import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ToastService } from '../../shared/components/toast/toast.service';
import { SettingsService } from './settings.service';
import {
  clipLabelText,
  formatLabelPriceLine,
  normalizeLabelTemplate,
  LabelTemplate,
  PRODUCT_LABEL_GAP_MM,
  PRODUCT_LABEL_HEIGHT_DOTS,
  PRODUCT_LABEL_HEIGHT_MM,
  PRODUCT_LABEL_DOTS_PER_MM,
  PRODUCT_LABEL_BARCODE_HEIGHT_DOTS,
  PRODUCT_LABEL_BARCODE_MODULE_WIDTH,
  PRODUCT_LABEL_CHARS_PER_LINE,
  PRODUCT_LABEL_COMPANY_MAX_CHARS,
  PRODUCT_LABEL_GAP_DOTS,
  PRODUCT_LABEL_LINE_SPACING,
  PRODUCT_LABEL_MARGIN_LEFT_DOTS,
  PRODUCT_LABEL_MARGIN_LEFT_MM,
  PRODUCT_LABEL_MARGIN_RIGHT_DOTS,
  PRODUCT_LABEL_MOTION_UNIT,
  PRODUCT_LABEL_NAME_MAX_CHARS,
  PRODUCT_LABEL_PITCH_DOTS,
  PRODUCT_LABEL_PRICE_MAX_CHARS,
  PRODUCT_LABEL_PRINT_DELAY_MS,
  PRODUCT_LABEL_ROW_Y,
  PRODUCT_LABEL_WIDTH_DOTS,
  PRODUCT_LABEL_WIDTH_MM
} from './label-spec';
import {
  renderRetailLabelRaster,
  retailLabelRasterToEscPos,
  retailLabelRasterToTsplHex
} from '../utils/retail-label-canvas';

// Type declarations for Web APIs
declare global {
  interface BluetoothRemoteGATTServer {
    connect(): Promise<BluetoothRemoteGATTServer>;
    disconnect(): void;
    getPrimaryService(service: string): Promise<BluetoothRemoteGATTService>;
    getPrimaryServices(): Promise<BluetoothRemoteGATTService[]>;
    connected: boolean;
  }

  interface BluetoothRemoteGATTService {
    getCharacteristic(characteristic: string): Promise<BluetoothRemoteGATTCharacteristic>;
    getCharacteristics(): Promise<BluetoothRemoteGATTCharacteristic[]>;
    uuid: string;
  }

  interface BluetoothRemoteGATTCharacteristic {
    writeValue(value: ArrayBuffer | ArrayBufferView): Promise<void>;
    writeValueWithoutResponse(value: ArrayBuffer | ArrayBufferView): Promise<void>;
    uuid: string;
    properties: {
      write: boolean;
      writeWithoutResponse: boolean;
      read: boolean;
      notify: boolean;
    };
  }

  interface BluetoothDevice {
    gatt?: BluetoothRemoteGATTServer | null;
    name?: string;
    id?: string;
  }
  
  interface SerialPort {
    readable?: ReadableStream<Uint8Array> | null;
    writable?: WritableStream<Uint8Array> | null;
    open(options: SerialOptions): Promise<void>;
    close(): Promise<void>;
  }

  interface SerialOptions {
    baudRate: number;
    dataBits?: number;
    stopBits?: number;
    parity?: 'none' | 'even' | 'odd';
    bufferSize?: number;
    flowControl?: 'none' | 'hardware';
  }
  
  interface Navigator {
    bluetooth?: {
      requestDevice(options: { acceptAllDevices?: boolean; filters?: Array<{ services?: string[] }>; optionalServices?: string[] }): Promise<BluetoothDevice>;
    };
    serial?: {
      requestPort(options?: { filters?: Array<{ usbVendorId?: number; usbProductId?: number }> }): Promise<SerialPort>;
      getPorts(): Promise<SerialPort[]>;
    };
  }
}

export type PaperSize = '2inch' | '3inch';
export type ConnectionType = 'bluetooth' | 'usb' | 'serial';
/** escpos = POSIFLOW / 2-inch thermal on label roll; tspl = TSC/Xprinter only */
export type LabelProtocol = 'tspl' | 'escpos';

/** Keys for A5 bill table columns – used for select/deselect in UI and in A4PrintService */
export type A5BillColumnKey = 'sno' | 'particulars' | 'mrp' | 'rateBeforeDisc' | 'discount' | 'qty' | 'amountAfterDisc';

export interface A5BillColumns {
  sno: boolean;
  particulars: boolean;
  mrp: boolean;
  /** Rate per product before discount – shown on A5 print/PDF when enabled (default true) */
  rateBeforeDisc?: boolean;
  discount: boolean;
  qty: boolean;
  amountAfterDisc: boolean;
}

export const A5_BILL_COLUMN_DEFAULTS: A5BillColumns = {
  sno: true,
  particulars: true,
  mrp: true,
  rateBeforeDisc: true,
  discount: true,
  qty: true,
  amountAfterDisc: true
};

/** A4 GST invoice table column keys – all on by default */
export type A4GstBillColumnKey = 'sno' | 'particulars' | 'hsn' | 'mrp' | 'rateBeforeDisc' | 'discount' | 'rateAfterDisc' | 'qty' | 'rate' | 'taxValue' | 'gstRate' | 'cgst' | 'sgst' | 'amountBeforeDisc' | 'amountAfterDisc';

export interface A4GstBillColumns {
  sno: boolean;
  particulars: boolean;
  hsn: boolean;
  mrp: boolean;
  rateBeforeDisc: boolean;
  discount: boolean;
  rateAfterDisc: boolean;
  qty: boolean;
  rate: boolean;
  taxValue: boolean;
  gstRate: boolean;
  cgst: boolean;
  sgst: boolean;
  amountBeforeDisc: boolean;
  amountAfterDisc: boolean;
}

export const A4_GST_BILL_COLUMN_DEFAULTS: A4GstBillColumns = {
  sno: true, particulars: true, hsn: true, mrp: true, rateBeforeDisc: true, discount: true, rateAfterDisc: true,
  qty: true, rate: true, taxValue: true, gstRate: true, cgst: true, sgst: true, amountBeforeDisc: true, amountAfterDisc: true
};

/** A4 Non-GST estimate table column keys – all on by default */
export type A4NonGstBillColumnKey = 'sno' | 'particulars' | 'mrp' | 'rateBeforeDisc' | 'discount' | 'rateAfterDisc' | 'qty' | 'amountBeforeDisc' | 'amountAfterDisc';

export interface A4NonGstBillColumns {
  sno: boolean;
  particulars: boolean;
  mrp: boolean;
  rateBeforeDisc: boolean;
  discount: boolean;
  rateAfterDisc: boolean;
  qty: boolean;
  amountBeforeDisc: boolean;
  amountAfterDisc: boolean;
}

export const A4_NON_GST_BILL_COLUMN_DEFAULTS: A4NonGstBillColumns = {
  sno: true, particulars: true, mrp: true, rateBeforeDisc: true, discount: true, rateAfterDisc: true,
  qty: true, amountBeforeDisc: true, amountAfterDisc: true
};

export interface PrinterSettings {
  /** When false, thermal/label printing is skipped. Default true. */
  thermalEnabled?: boolean;
  /** When false, A4 print and PDF download are hidden/disabled. Default true. */
  a4Enabled?: boolean;
  /** When false, A5 print and PDF download are hidden/disabled. Default true. */
  a5Enabled?: boolean;
  /** @deprecated Use thermalEnabled */
  enabled?: boolean;
  paperSize: PaperSize;
  connectionType: ConnectionType;
  /** Label printer command set for 50×25mm product labels */
  labelProtocol?: LabelProtocol;
  /** Fine-tune label feed in mm (+ = feed more, − = feed less). For alignment on POSIFLOW etc. */
  labelFeedOffsetMm?: number;
  /** Which columns to show on A5 print/PDF; only used when generating A5 bills */
  a5BillColumns?: Partial<A5BillColumns>;
  /** Which columns to show on A4 GST invoice; default all on */
  a4GstBillColumns?: Partial<A4GstBillColumns>;
  /** Which columns to show on A4 Non-GST estimate; default all on */
  a4NonGstBillColumns?: Partial<A4NonGstBillColumns>;
}

export interface BillData {
  bill_number: string;
  customer_name?: string;
  customer_phone?: string;
  customer_balance?: number; // Current balance before this bill
  customer_balance_after?: number; // Balance after adding this bill amount
  subtotal: number;
  discount_amount: number;
  discount_type?: 'fixed' | 'percentage';
  discount_value?: number;
  gst_amount: number;
  total_amount: number;
  payment_mode: string;
  created_at: string;
  route_off?: number;
  route_off_mode?: 'plus' | 'minus' | null;
  original_total?: number;
  rounded_total?: number;
  rounding_difference?: number;
  items: Array<{
    product_name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    mrp?: number;
    gst_rate?: number;
    gst_amount?: number;
    discount_amount?: number;
    discount_type?: 'fixed' | 'percentage';
    discount_value?: number;
  }>;
}

export interface BarcodeLabelData {
  name: string;
  barcode: string;
  companyName?: string;
  companyLogo?: string;
  companySlogan?: string;
  currencySymbol?: string;
  template?: LabelTemplate;
  price?: number;
  mrp?: number;
  sku?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PrinterService {
  private bluetoothDevice: BluetoothDevice | null = null;
  private serialPort: SerialPort | null = null;
  private thermalLogoCache = new Map<string, number[]>();
  private readonly STORAGE_KEY = 'printer_settings';
  private readonly SERIAL_CONFIG_KEY = 'last_serial_config';
  private isBrowser: boolean;

  constructor(
    private toast: ToastService,
    private settingsService: SettingsService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Get printer settings from localStorage.
   * A5 column preferences are merged with defaults (missing = shown).
   */
  getSettings(): PrinterSettings {
    if (!this.isBrowser) {
      return {
        thermalEnabled: true,
        a4Enabled: true,
        a5Enabled: true,
        paperSize: '2inch',
        connectionType: 'bluetooth',
        labelProtocol: 'escpos',
        a5BillColumns: { ...A5_BILL_COLUMN_DEFAULTS },
        a4GstBillColumns: { ...A4_GST_BILL_COLUMN_DEFAULTS },
        a4NonGstBillColumns: { ...A4_NON_GST_BILL_COLUMN_DEFAULTS }
      };
    }

    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as PrinterSettings;
        const a5BillColumns: A5BillColumns = {
          ...A5_BILL_COLUMN_DEFAULTS,
          ...(parsed.a5BillColumns || {})
        };
        const a4GstBillColumns: A4GstBillColumns = {
          ...A4_GST_BILL_COLUMN_DEFAULTS,
          ...(parsed.a4GstBillColumns || {})
        };
        const a4NonGstBillColumns: A4NonGstBillColumns = {
          ...A4_NON_GST_BILL_COLUMN_DEFAULTS,
          ...(parsed.a4NonGstBillColumns || {})
        };
        const flags = this.resolveFormatFlags(parsed);
        return {
          ...parsed,
          ...flags,
          labelProtocol: parsed.labelProtocol === 'tspl' ? 'tspl' : 'escpos',
          labelFeedOffsetMm: typeof parsed.labelFeedOffsetMm === 'number' ? parsed.labelFeedOffsetMm : 0,
          a5BillColumns,
          a4GstBillColumns,
          a4NonGstBillColumns
        };
      } catch {
        // Invalid JSON, return defaults
      }
    }
    return {
      thermalEnabled: true,
      a4Enabled: true,
      a5Enabled: true,
      paperSize: '2inch',
      connectionType: 'bluetooth',
      labelProtocol: 'escpos',
      a5BillColumns: { ...A5_BILL_COLUMN_DEFAULTS },
      a4GstBillColumns: { ...A4_GST_BILL_COLUMN_DEFAULTS },
      a4NonGstBillColumns: { ...A4_NON_GST_BILL_COLUMN_DEFAULTS }
    };
  }

  private resolveFormatFlags(parsed: Partial<PrinterSettings>): Pick<PrinterSettings, 'thermalEnabled' | 'a4Enabled' | 'a5Enabled'> {
    const thermalEnabled = parsed.thermalEnabled !== undefined
      ? parsed.thermalEnabled !== false
      : parsed.enabled !== false;
    return {
      thermalEnabled,
      a4Enabled: parsed.a4Enabled !== false,
      a5Enabled: parsed.a5Enabled !== false
    };
  }

  /** Whether thermal/label printing is enabled. */
  isThermalEnabled(): boolean {
    return this.getSettings().thermalEnabled !== false;
  }

  /** Whether A4 print/PDF is enabled. */
  isA4Enabled(): boolean {
    return this.getSettings().a4Enabled !== false;
  }

  /** Whether A5 print/PDF is enabled. */
  isA5Enabled(): boolean {
    return this.getSettings().a5Enabled !== false;
  }

  /** @deprecated Use isThermalEnabled() */
  isEnabled(): boolean {
    return this.isThermalEnabled();
  }

  /**
   * Get A5 bill column visibility (for print/PDF). Always returns full A5BillColumns with defaults merged.
   */
  getA5BillColumns(): A5BillColumns {
    const settings = this.getSettings();
    return {
      ...A5_BILL_COLUMN_DEFAULTS,
      ...(settings.a5BillColumns || {})
    };
  }

  /**
   * Get A4 GST bill column visibility; defaults all on.
   */
  getA4GstBillColumns(): A4GstBillColumns {
    const settings = this.getSettings();
    return {
      ...A4_GST_BILL_COLUMN_DEFAULTS,
      ...(settings.a4GstBillColumns || {})
    };
  }

  /**
   * Get A4 Non-GST bill column visibility; defaults all on.
   */
  getA4NonGstBillColumns(): A4NonGstBillColumns {
    const settings = this.getSettings();
    return {
      ...A4_NON_GST_BILL_COLUMN_DEFAULTS,
      ...(settings.a4NonGstBillColumns || {})
    };
  }

  /**
   * Save printer settings to localStorage.
   * Pass current getSettings() and override a5BillColumns to persist column choices.
   */
  saveSettings(settings: PrinterSettings): void {
    if (!this.isBrowser) {
      return;
    }
    const flags = this.resolveFormatFlags(settings);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({ ...settings, ...flags }));
  }

  /**
   * Generate ESC/POS formatted bill bytes
   */
  async generateEscPosBill(billData: BillData, paperSize: PaperSize): Promise<Uint8Array> {
    const charsPerLine = paperSize === '2inch' ? 32 : 48;
    const commands: number[] = [];

    console.log('[PrinterService] Generating bill for:', billData.bill_number);
    console.log('[PrinterService] Items count:', billData.items.length);

    // Get company settings
    const settings = this.settingsService.getSettings();
    const companyName = settings.company_name || 'Bilali Islamic Shopping';
    const companySlogan = settings.company_slogan;
    const companyAddress = 'Aranthangi';
    const companyPhone = settings.phone || '';
    const thanksMessage = settings.thanks_message || 'Thank you for shopping with us!\nVisit again';
    const thanksLines = thanksMessage.split('\n').filter(line => line.trim() !== '');

    // Initialize printer
    commands.push(0x1B, 0x40); // ESC @ - Initialize

    const companyLogo = (settings.company_logo || '').trim();
    const hasLogo = !!companyLogo;
    if (companyLogo) {
      try {
        // Keep thermal logo compact for faster print transfer while preserving readability.
        const logoMaxWidth = paperSize === '2inch' ? 360 : 560;
        const logoMaxHeight = paperSize === '2inch' ? 145 : 205;
        const cacheKey = `${paperSize}:${logoMaxWidth}:${logoMaxHeight}:${companyLogo.length}:${companyLogo.slice(0, 64)}`;
        let logoBytes = this.thermalLogoCache.get(cacheKey);
        if (!logoBytes) {
          logoBytes = await this.getEscPosImageBytes(companyLogo, logoMaxWidth, logoMaxHeight);
          this.thermalLogoCache.set(cacheKey, logoBytes);
        }
        if (logoBytes.length > 0) {
          commands.push(0x1B, 0x61, 0x01); // ESC a 1 - Center align
          commands.push(...logoBytes);
        }
      } catch (error) {
        console.warn('[PrinterService] Unable to render logo for thermal print:', error);
      }
    }

    // Center align and bold for header
    commands.push(0x1B, 0x61, 0x01); // ESC a 1 - Center align
    // Avoid duplicate branding on thermal print: if logo is present, skip name line.
    if (!hasLogo) {
      commands.push(0x1B, 0x45, 0x01); // ESC E 1 - Bold on
      this.addText(commands, companyName, charsPerLine);
      commands.push(0x0A); // Line feed
      commands.push(0x1B, 0x45, 0x00); // Bold off
    }
    
    // Add company address if available
    if (companyAddress) {
      this.addText(commands, companyAddress, charsPerLine);
      commands.push(0x0A); // Line feed
    }
    
    // Add company phone if available
    if (companyPhone) {
      const phoneLabel = 'Ph:';
      const phoneText = `${phoneLabel} ${companyPhone}`;
      this.addText(commands, phoneText, charsPerLine);
      commands.push(0x0A); // Line feed
    }
    
    // Add company slogan if available
    if (companySlogan) {
      this.addText(commands, companySlogan, charsPerLine);
      commands.push(0x0A); // Line feed
    }

    // Left align
    commands.push(0x1B, 0x61, 0x00); // ESC a 0 - Left align

    // Bill info - same format for both
    const billNumLabel = 'Bill#';
    const billNumText = `${billNumLabel} ${billData.bill_number}`;
    this.addLine(commands, this.truncate(billNumText, charsPerLine), charsPerLine);
    
    const date = new Date(billData.created_at);
    const dateStr = date.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    this.addLine(commands, `Date: ${this.truncate(dateStr, charsPerLine - 6)}`, charsPerLine);
    
    if (billData.customer_name) {
      const custLabel = 'Cust:';
      const custText = `${custLabel} ${billData.customer_name}`;
      this.addLine(commands, this.truncate(custText, charsPerLine), charsPerLine);
    }
    if (billData.customer_phone) {
      const phoneText = `Phone: ${billData.customer_phone}`;
      this.addLine(commands, this.truncate(phoneText, charsPerLine), charsPerLine);
    }

    // Divider
    this.addDivider(commands, charsPerLine);
    // No extra line feed - same for both formats

    // Items header - Optimized for 32-char printer
    commands.push(0x1B, 0x61, 0x00); // Left align
    commands.push(0x1B, 0x45, 0x01); // Bold on
    
    // Column headers matching the item format (4+7+3+6+8 = 28 + 4 spaces = 32)
    const headerLine = 'MRP  Item    Qty   Rate    Amt';
    this.addText(commands, headerLine, charsPerLine);
    commands.push(0x0A);
    commands.push(0x1B, 0x45, 0x00); // Bold off
    this.addDivider(commands, charsPerLine);
    // No extra line feed - same for both formats

    // Items list - Optimized for 32-char 2-inch printer
    billData.items.forEach(item => {
      // Line 1: MRP Item Qty Rate Amt
      // Line 2: Full item name if needed
      // Line 3: Discount if exists
      
      // Column widths for 32-char printer (total: 32) - Amount 8 chars so 3500.00+ fits fully
      const mrpWidth = 4;       // MRP: "10" (no decimal)
      const itemWidth = 7;      // Item: shortened (7 to allow Amount 8)
      const qtyWidth = 3;       // Qty: "500"
      const rateWidth = 6;      // Rate: "7.30" with .00
      const amountWidth = 8;    // Amount: "3500.00" / "99999.99" - full amount visible
      // Total: 4+7+3+6+8 = 28 + 4 spaces = 32 chars
      
      // Get values - use total_price if available (after discount), else qty * rate
      const mrpValue = (item.mrp !== undefined && item.mrp !== null && item.mrp > 0) ? item.mrp : 0;
      const itemName = item.product_name || '';
      const qty = item.quantity;
      const rate = item.unit_price;
      const amount = typeof item.total_price === 'number' ? item.total_price : (qty * rate);
      
      // Build columns: ensure exact width so line never exceeds 32 (prevents truncation with "...")
      const mrpCol = mrpValue > 0 ? mrpValue.toFixed(0).slice(-mrpWidth).padStart(mrpWidth) : ''.padEnd(mrpWidth);
      const itemCol = itemName.substring(0, itemWidth).padEnd(itemWidth);
      const qtyCol = qty.toString().padStart(qtyWidth).slice(-qtyWidth);
      const rateStr = rate.toFixed(2);
      const rateCol = rateStr.length > rateWidth ? rateStr.slice(-rateWidth) : rateStr.padStart(rateWidth);
      const amountStr = amount.toFixed(2);
      const amountCol = amountStr.length > amountWidth ? amountStr.slice(-amountWidth) : amountStr.padStart(amountWidth);
      
      // Build first line: MRP Item Qty Rate Amt
      const firstLine = `${mrpCol} ${itemCol} ${qtyCol} ${rateCol} ${amountCol}`;
      this.addLine(commands, firstLine, charsPerLine);
      
      // Show full item name on next line(s) if truncated - wrap to fit width, no truncation
      if (itemName.length > itemWidth) {
        const remainingName = itemName.substring(itemWidth);
        const indent = '  ';
        const maxChunk = charsPerLine - indent.length;
        for (let i = 0; i < remainingName.length; i += maxChunk) {
          const chunk = remainingName.substring(i, i + maxChunk);
          this.addLine(commands, indent + chunk, charsPerLine);
        }
      }
      
      // Show discount on separate line if exists
      if (item.discount_amount && item.discount_amount > 0) {
        // Use stored discount_value if available, otherwise calculate from amount
        let discountDisplay = '';
        if (item.discount_type === 'percentage' && item.discount_value && item.discount_value > 0) {
          // Use the actual stored percentage value
          discountDisplay = `  Discount (${item.discount_value}%): -Rs.${item.discount_amount.toFixed(2)}`;
        } else if (item.discount_type === 'fixed') {
          discountDisplay = `  Discount: -Rs.${item.discount_amount.toFixed(2)}`;
        } else {
          // Fallback: For old bills without discount_value, try to calculate from total_price
          // total_price should be (qty * rate) - discount, so we can derive the percentage
          // But we need to account for GST. Use the discount amount directly.
          // Calculate based on amount BEFORE discount
          const amountBeforeDiscount = item.total_price + item.discount_amount;
          const discPct = amountBeforeDiscount > 0 
            ? ((item.discount_amount / amountBeforeDiscount) * 100).toFixed(1)
            : '0.0';
          discountDisplay = `  Discount (${discPct}%): -Rs.${item.discount_amount.toFixed(2)}`;
        }
        this.addLine(commands, discountDisplay, charsPerLine);
      }
    });

    // Divider after items
    this.addDivider(commands, charsPerLine);
    commands.push(0x0A); // Line feed before totals

    // Calculate subtotal BEFORE any discounts (qty * rate for all items)
    const subtotalBeforeDiscounts = billData.items.reduce((sum, item) => {
      return sum + (item.quantity * item.unit_price);
    }, 0);
    
    // Calculate total discount: bill-level + item-level
    const billLevelDiscount = billData.discount_amount || 0;
    const itemLevelDiscount = billData.items.reduce((sum, item) => {
      return sum + (item.discount_amount || 0);
    }, 0);
    const totalDiscountAmount = billLevelDiscount + itemLevelDiscount;

    // Totals section - Sub Total (before ANY discounts)
    const subtotalLabel = 'Sub Total';
    const subtotalPadded = this.formatAmountLine(subtotalLabel, subtotalBeforeDiscounts, charsPerLine);
    this.addLine(commands, subtotalPadded, charsPerLine);
    
    // Show total discount if any (always show if > 0)
    if (totalDiscountAmount > 0) {
      // Use stored discount value/type if available
      let discountLabel = 'Discount';
      if (billData.discount_type === 'percentage' && billData.discount_value && billLevelDiscount > 0) {
        discountLabel = `Discount (${billData.discount_value}%)`;
      } else if (subtotalBeforeDiscounts > 0) {
        // Calculate percentage from amounts
        const discountPercent = ((totalDiscountAmount / subtotalBeforeDiscounts) * 100).toFixed(1);
        discountLabel = `Discount (${discountPercent}%)`;
      }
      const discountPadded = this.formatAmountLine(discountLabel, -totalDiscountAmount, charsPerLine);
      this.addLine(commands, discountPadded, charsPerLine);
    }
    
    // Round-off (if route off is enabled)
    if (billData.route_off && billData.rounding_difference !== undefined && billData.rounding_difference !== 0) {
      const roundOffLabel = 'Round-off';
      const roundOffPadded = this.formatAmountLine(roundOffLabel, billData.rounding_difference, charsPerLine);
      this.addLine(commands, roundOffPadded, charsPerLine);
    }
    
    // Net Amount - bold
    // Use rounded_total if route_off is enabled, otherwise use total_amount
    commands.push(0x1B, 0x45, 0x01); // Bold on
    const netAmountLabel = 'Net Amount';
    const finalAmount = (billData.route_off && billData.rounded_total !== undefined) 
      ? billData.rounded_total 
      : billData.total_amount;
    const netAmountPadded = this.formatAmountLine(netAmountLabel, finalAmount, charsPerLine);
    this.addText(commands, netAmountPadded, charsPerLine);
    commands.push(0x0A);
    commands.push(0x1B, 0x45, 0x00); // Bold off

    // Payment section - Same format for both
    // Divider before payment section
    this.addDivider(commands, charsPerLine);
    // No extra line feed
    
    // RECEIVED (if payment received, otherwise 0)
    const receivedLabel = 'RECEIVED';
    const receivedAmount = 0; // You may want to track actual received amount
    const receivedPadded = this.formatAmountLine(receivedLabel, receivedAmount, charsPerLine);
    this.addLine(commands, receivedPadded, charsPerLine);
    
    // Balance - bold
    commands.push(0x1B, 0x45, 0x01); // Bold on
    const balanceLabel = 'Balance';
    // Use rounded_total if route_off is enabled
    const totalForBalance = (billData.route_off && billData.rounded_total !== undefined) 
      ? billData.rounded_total 
      : billData.total_amount;
    const balanceAmount = totalForBalance - receivedAmount; // Calculate balance
    const balancePadded = this.formatAmountLine(balanceLabel, balanceAmount, charsPerLine);
    this.addText(commands, balancePadded, charsPerLine);
    commands.push(0x0A);
    commands.push(0x1B, 0x45, 0x00); // Bold off
    
    // Divider after payment section
    this.addDivider(commands, charsPerLine);
    commands.push(0x0A); // Line feed before customer balance / footer

    // Customer Balance Information (if customer exists and balance is available)
    if (billData.customer_name && billData.customer_balance !== undefined && billData.customer_balance !== null) {
      commands.push(0x0A);
      this.addDivider(commands, charsPerLine);
      commands.push(0x0A);
      commands.push(0x1B, 0x61, 0x00); // Left align
      commands.push(0x1B, 0x45, 0x01); // Bold on
      
      // Current Balance (Pending Amount)
      const currentBalanceLabel = paperSize === '2inch' ? 'Curr Bal:' : 'Current Balance:';
      const currentBalancePadded = this.formatAmountLine(currentBalanceLabel, billData.customer_balance, charsPerLine);
      this.addText(commands, currentBalancePadded, charsPerLine);
      commands.push(0x0A);
      
      // Billing Amount (This Bill) - use rounded total if route off enabled
      const billingLabel = paperSize === '2inch' ? 'Bill Amt:' : 'Billing Amount:';
      const billingAmount = (billData.route_off && billData.rounded_total !== undefined) 
        ? billData.rounded_total 
        : billData.total_amount;
      const billingPadded = this.formatAmountLine(billingLabel, billingAmount, charsPerLine);
      this.addText(commands, billingPadded, charsPerLine);
      commands.push(0x0A);
      
      // New Balance (Current + Billing)
      const newBalance = (billData.customer_balance_after !== undefined && billData.customer_balance_after !== null) 
        ? billData.customer_balance_after 
        : billData.customer_balance + billingAmount;
      const newBalanceLabel = paperSize === '2inch' ? 'New Bal:' : 'New Balance:';
      commands.push(0x1B, 0x45, 0x01); // Bold on (ensure bold)
      const newBalancePadded = this.formatAmountLine(newBalanceLabel, newBalance, charsPerLine);
      this.addText(commands, newBalancePadded, charsPerLine);
      commands.push(0x0A);
      
      commands.push(0x1B, 0x45, 0x00); // Bold off
      commands.push(0x0A);
    }

    // Footer - blank line then thank you / developer info
    commands.push(0x0A); // Blank line before footer
    thanksLines.forEach(line => {
      this.addLine(commands, line, charsPerLine);
    });
    
    // Developer information - always added to all receipts
    // commands.push(0x1B, 0x61, 0x01); // Center align
    // this.addLine(commands, 'Thank you for your purchase', charsPerLine);
    this.addLine(commands, 'Developed by KRM INFO TECH', charsPerLine);
    this.addLine(commands, 'Call : 6369311293', charsPerLine);
    commands.push(0x0A); // Single line feed - same for both formats
    
    // Feed paper before cut (minimal spacing)
    commands.push(0x1B, 0x4A, 0x30); // ESC J 48 - Feed 48 dots (about 6mm) - just enough to clear cutter
    
    // Paper cut - SINGLE cut command only
    commands.push(0x1D, 0x56, 0x01); // GS V 1 - Partial cut (recommended for most thermal printers)

    return new Uint8Array(commands);
  }

  /**
   * Generate bytes for one product label (single sticker).
   */
  async generateBarcodeLabelBytes(labelData: BarcodeLabelData, protocol: LabelProtocol, _quantity = 1): Promise<Uint8Array> {
    const template = normalizeLabelTemplate(labelData.template);
    if (protocol === 'tspl') {
      if (template === 'retail') {
        return new TextEncoder().encode(await this.buildTsplRetailJob(labelData));
      }
      return new TextEncoder().encode(this.buildTsplSingleJob(labelData));
    }
    if (template === 'retail') {
      return this.generateEscPosRetailLabel(labelData);
    }
    return this.generateEscPosSingleLabel(labelData);
  }

  /**
   * TSPL — gap sensor advances each PRINT to the next sticker top (50×25mm, 3mm gap).
   */
  generateTsplBarcodeLabel(labelData: BarcodeLabelData, _quantity = 1): Uint8Array {
    return new TextEncoder().encode(this.buildTsplSingleJob(labelData));
  }

  private buildTsplSingleJob(labelData: BarcodeLabelData): string {
    const lines = this.buildTsplSetupLines(true);
    lines.push(...this.buildTsplLabelContentLines(labelData));
    lines.push('PRINT 1,1');
    return lines.join('\r\n') + '\r\n';
  }

  private buildTsplSetupLines(classicCentered = false): string[] {
    return [
      `SIZE ${PRODUCT_LABEL_WIDTH_MM} mm, ${PRODUCT_LABEL_HEIGHT_MM} mm`,
      `GAP ${PRODUCT_LABEL_GAP_MM} mm, 0 mm`,
      'DIRECTION 1',
      'REFERENCE 0,0',
      classicCentered ? 'OFFSET 0 mm' : `OFFSET ${PRODUCT_LABEL_MARGIN_LEFT_MM} mm`,
      'CODEPAGE UTF-8',
      'SPEED 4',
      'DENSITY 8',
      'SET TEAR ON',
      `GAPDETECT ${PRODUCT_LABEL_HEIGHT_DOTS},${PRODUCT_LABEL_GAP_DOTS}`,
    ];
  }

  private buildTsplLabelContentLines(labelData: BarcodeLabelData): string[] {
    const barcode = (labelData.barcode || '').trim();
    const name = this.sanitizeLabelText(clipLabelText(labelData.name, PRODUCT_LABEL_NAME_MAX_CHARS));
    const priceLine = clipLabelText(formatLabelPriceLine(labelData.price, labelData.mrp), PRODUCT_LABEL_PRICE_MAX_CHARS);
    const companyName = this.sanitizeLabelText(clipLabelText(labelData.companyName || '', PRODUCT_LABEL_COMPANY_MAX_CHARS));
    const barcodeH = PRODUCT_LABEL_BARCODE_HEIGHT_DOTS;

    const lines: string[] = ['CLS'];

    if (companyName) {
      lines.push(this.tsplCenteredText(companyName, PRODUCT_LABEL_ROW_Y.company, '1', 10));
    }

    lines.push(this.tsplCenteredText(name, PRODUCT_LABEL_ROW_Y.name, '2', 14));

    if (priceLine) {
      lines.push(this.tsplCenteredText(priceLine, PRODUCT_LABEL_ROW_Y.price, '1', 10));
    }

    const barcodeX = this.tsplCenteredBarcodeX(barcode);
    lines.push(
      `BARCODE ${barcodeX},${PRODUCT_LABEL_ROW_Y.barcode},"128",${barcodeH},1,0,2,2,"${this.escapeTspl(barcode)}"`,
      this.tsplCenteredText(barcode, PRODUCT_LABEL_ROW_Y.barcodeText, '0', 8)
    );

    return lines;
  }

  private tsplCenteredText(text: string, y: number, font: string, charWidthDots: number): string {
    const x = Math.max(0, Math.round((PRODUCT_LABEL_WIDTH_DOTS - text.length * charWidthDots) / 2));
    return `TEXT ${x},${y},"${font}",0,1,1,"${this.escapeTspl(text)}"`;
  }

  private tsplCenteredBarcodeX(barcode: string): number {
    const modules = (barcode.length + 3) * 11;
    const widthDots = modules * 2;
    return Math.max(0, Math.round((PRODUCT_LABEL_WIDTH_DOTS - widthDots) / 2));
  }

  private async buildTsplRetailJob(labelData: BarcodeLabelData): Promise<string> {
    const raster = await renderRetailLabelRaster(labelData);
    const widthBytes = raster.width / 8;
    const hex = retailLabelRasterToTsplHex(raster);
    const lines = this.buildTsplSetupLines(false);
    lines.push('CLS');
    if (hex) {
      lines.push(`BITMAP 0,0,${widthBytes},${raster.height},0,${hex}`);
    }
    lines.push('PRINT 1,1');
    return lines.join('\r\n') + '\r\n';
  }

  /**
   * ESC/POS — fixed row slots, then feed to end of 25mm label + 3mm gap (one pitch).
   */
  generateEscPos50x25BarcodeLabel(labelData: BarcodeLabelData, _quantity = 1): Promise<Uint8Array> {
    return this.generateBarcodeLabelBytes(labelData, 'escpos', _quantity);
  }

  private async generateEscPosRetailLabel(labelData: BarcodeLabelData): Promise<Uint8Array> {
    const commands: number[] = [];
    commands.push(0x1D, 0x50, PRODUCT_LABEL_MOTION_UNIT, PRODUCT_LABEL_MOTION_UNIT);
    const raster = await renderRetailLabelRaster(labelData);
    commands.push(...retailLabelRasterToEscPos(raster));
    this.finishEscPosFeedModeLabel(commands, raster.height || PRODUCT_LABEL_HEIGHT_DOTS);
    return new Uint8Array(commands);
  }

  private async generateEscPosSingleLabel(labelData: BarcodeLabelData): Promise<Uint8Array> {
    const commands: number[] = [];
    commands.push(0x1D, 0x50, PRODUCT_LABEL_MOTION_UNIT, PRODUCT_LABEL_MOTION_UNIT);
    this.setEscPosLeftMargin(commands, 0);
    commands.push(0x1B, 0x33, PRODUCT_LABEL_LINE_SPACING);
    commands.push(0x1B, 0x61, 0x01);
    commands.push(0x1B, 0x4D, 0x00);
    const contentBottomY = await this.appendEscPosFeedModeContent(commands, labelData);
    this.finishEscPosFeedModeLabel(commands, contentBottomY);
    return new Uint8Array(commands);
  }

  private feedToRow(commands: number[], currentY: number, targetY: number): number {
    const delta = targetY - currentY;
    if (delta > 0) {
      this.feedDots(commands, delta);
    }
    return targetY;
  }

  private async appendEscPosFeedModeContent(commands: number[], labelData: BarcodeLabelData): Promise<number> {
    const barcode = (labelData.barcode || '').trim();
    const companyName = this.sanitizeLabelText(clipLabelText(labelData.companyName || '', PRODUCT_LABEL_COMPANY_MAX_CHARS));
    const name = this.sanitizeLabelText(clipLabelText(labelData.name, PRODUCT_LABEL_NAME_MAX_CHARS));
    const priceLine = clipLabelText(formatLabelPriceLine(labelData.price, labelData.mrp), PRODUCT_LABEL_PRICE_MAX_CHARS);
    const maxTextWidthDots = PRODUCT_LABEL_WIDTH_DOTS;

    let y = 0;

    const printLine = async (text: string, fontSizeDots: number, bold = false): Promise<void> => {
      if (!text) {
        return;
      }
      if (this.needsRasterLabelText(text)) {
        const raster = await this.renderEscPosLabelTextRaster(text, maxTextWidthDots, fontSizeDots, bold, true);
        if (raster.commands.length > 0) {
          y = this.feedToRow(commands, y, y);
          commands.push(...raster.commands);
          y += raster.heightDots + 2;
        }
        return;
      }
      this.addLabelText(commands, text);
      commands.push(0x0A);
      y += PRODUCT_LABEL_LINE_SPACING;
    };

    if (companyName) {
      await printLine(companyName, 15);
    }
    await printLine(name, 18, true);
    if (priceLine) {
      await printLine(priceLine, 17, true);
    }

    y = this.feedToRow(commands, y, y + 2);

    commands.push(0x1D, 0x68, PRODUCT_LABEL_BARCODE_HEIGHT_DOTS);
    commands.push(0x1D, 0x77, PRODUCT_LABEL_BARCODE_MODULE_WIDTH);
    commands.push(0x1D, 0x48, 0x00);

    const barcodeBytes: number[] = [];
    for (let i = 0; i < barcode.length; i++) {
      const code = barcode.charCodeAt(i);
      if (code >= 32 && code <= 126) {
        barcodeBytes.push(code);
      }
    }

    if (barcodeBytes.length > 0 && barcodeBytes.length <= 255) {
      commands.push(0x1D, 0x6B, 0x49, barcodeBytes.length);
      commands.push(...barcodeBytes);
      y += PRODUCT_LABEL_BARCODE_HEIGHT_DOTS;
    }

    y = this.feedToRow(commands, y, y + 4);
    this.addLabelText(commands, clipLabelText(barcode, PRODUCT_LABEL_CHARS_PER_LINE));
    y += 14;

    return y;
  }

  /** Advance paper: rest of 25mm label, then 3mm gap → next sticker top. */
  private finishEscPosFeedModeLabel(commands: number[], contentBottomY: number): void {
    const padToLabelEnd = PRODUCT_LABEL_HEIGHT_DOTS - contentBottomY;
    if (padToLabelEnd > 0) {
      this.feedDots(commands, padToLabelEnd);
    }
    const gapFeed = PRODUCT_LABEL_GAP_DOTS + this.getLabelFeedOffsetDots();
    if (gapFeed > 0) {
      this.feedDots(commands, gapFeed);
    }
  }

  private getLabelFeedOffsetDots(): number {
    const mm = Number(this.getSettings().labelFeedOffsetMm ?? 0);
    if (isNaN(mm)) {
      return 0;
    }
    return Math.round(Math.max(-5, Math.min(5, mm)) * PRODUCT_LABEL_DOTS_PER_MM);
  }

  private setEscPosLeftMargin(commands: number[], dots: number): void {
    const margin = Math.max(0, Math.round(dots));
    commands.push(0x1D, 0x4C, margin & 0xff, (margin >> 8) & 0xff);
  }

  private feedDots(commands: number[], dots: number): void {
    let remaining = Math.max(0, Math.round(dots));
    while (remaining > 0) {
      const chunk = Math.min(255, remaining);
      commands.push(0x1B, 0x4A, chunk);
      remaining -= chunk;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * @deprecated Bulk jobs are sent one label at a time via printBarcodeLabelsBulk.
   */
  async generateBarcodeLabelsBulkBytes(labels: BarcodeLabelData[], protocol: LabelProtocol, quantityPerProduct = 1): Promise<Uint8Array> {
    const validLabels = labels
      .map(label => ({ ...label, barcode: (label.barcode || label.sku || '').trim() }))
      .filter(label => !!label.barcode);
    if (validLabels.length === 0) {
      return new Uint8Array(0);
    }
    return this.generateBarcodeLabelBytes(validLabels[0], protocol, quantityPerProduct);
  }

  /** Tamil / non-Latin text must be rasterized — ESC/POS fonts are single-byte only. */
  private needsRasterLabelText(text: string): boolean {
    return /[^\u0000-\u00FF]/.test(text || '');
  }

  private async renderEscPosLabelTextRaster(
    text: string,
    maxWidthDots: number,
    fontSizeDots: number,
    bold = false,
    centerOnLabel = false
  ): Promise<{ commands: number[]; heightDots: number }> {
    if (!this.isBrowser || !text) {
      return { commands: [], heightDots: 0 };
    }

    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    if (!measureCtx) {
      return { commands: [], heightDots: 0 };
    }

    const fontSizePx = Math.max(10, Math.round(fontSizeDots));
    const fontFamily = '"Nirmala UI", "Latha", "Tamil MN", "Noto Sans Tamil", sans-serif';
    const font = `${bold ? 'bold ' : ''}${fontSizePx}px ${fontFamily}`;
    measureCtx.font = font;

    const textWidth = Math.ceil(measureCtx.measureText(text).width);
    const rawWidth = centerOnLabel
      ? (maxWidthDots % 8 === 0 ? maxWidthDots : maxWidthDots + (8 - (maxWidthDots % 8)))
      : (Math.min(maxWidthDots, Math.max(8, textWidth + 4)) % 8 === 0
        ? Math.min(maxWidthDots, Math.max(8, textWidth + 4))
        : Math.min(maxWidthDots, Math.max(8, textWidth + 4)) + (8 - (Math.min(maxWidthDots, Math.max(8, textWidth + 4)) % 8)));
    const rawHeight = Math.ceil(fontSizePx * 1.3);
    const width = rawWidth;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = rawHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return { commands: [], heightDots: 0 };
    }

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, rawHeight);
    ctx.fillStyle = '#000000';
    ctx.font = font;
    ctx.textBaseline = 'top';
    if (centerOnLabel) {
      ctx.textAlign = 'center';
      ctx.fillText(text, width / 2, 1);
    } else {
      ctx.fillText(text, 0, 1);
    }

    return this.canvasToEscPosRasterCommands(canvas);
  }

  private canvasToEscPosRasterCommands(canvas: HTMLCanvasElement): { commands: number[]; heightDots: number } {
    const width = canvas.width;
    const height = canvas.height;
    const ctx = canvas.getContext('2d');
    if (!ctx || width <= 0 || height <= 0) {
      return { commands: [], heightDots: 0 };
    }

    const imageData = ctx.getImageData(0, 0, width, height).data;
    const bytesPerRow = width / 8;
    const rasterBytes: number[] = [];

    for (let y = 0; y < height; y++) {
      for (let xByte = 0; xByte < bytesPerRow; xByte++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = (xByte * 8) + bit;
          const index = ((y * width) + x) * 4;
          const r = imageData[index];
          const g = imageData[index + 1];
          const b = imageData[index + 2];
          const alpha = imageData[index + 3];
          const luminance = (0.299 * r) + (0.587 * g) + (0.114 * b);
          const isBlack = alpha > 10 && luminance < 190;
          if (isBlack) {
            byte |= (0x80 >> bit);
          }
        }
        rasterBytes.push(byte);
      }
    }

    const xL = bytesPerRow & 0xFF;
    const xH = (bytesPerRow >> 8) & 0xFF;
    const yL = height & 0xFF;
    const yH = (height >> 8) & 0xFF;

    return {
      commands: [0x1D, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...rasterBytes],
      heightDots: height
    };
  }

  private escapeTspl(text: string): string {
    return (text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /** Normalize whitespace for label text (keeps Tamil / Unicode). */
  private sanitizeLabelText(text: string): string {
    return (text || '')
      .replace(/[\r\n\t]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** @deprecated Use generateBarcodeLabelBytes */
  async generateEscPosBarcodeLabel(labelData: BarcodeLabelData, _paperSize: PaperSize, quantity = 1): Promise<Uint8Array> {
    return this.generateBarcodeLabelBytes(labelData, 'escpos', quantity);
  }

  /** @deprecated Use generateBarcodeLabelsBulkBytes */
  async generateEscPosBarcodeLabelsBulk(labels: BarcodeLabelData[], _paperSize: PaperSize, quantityPerProduct = 1): Promise<Uint8Array> {
    return this.generateBarcodeLabelsBulkBytes(labels, 'escpos', quantityPerProduct);
  }

  /**
   * Ensure printer is connected before printing.
   */
  private async ensurePrinterReady(): Promise<boolean> {
    const settings = this.getSettings();

    if (!this.isThermalEnabled()) {
      this.toast.info('Thermal printer is disabled. Enable it in Printer Settings or use A4/A5 print.');
      return false;
    }

    if (settings.connectionType === 'bluetooth') {
      if (!this.bluetoothDevice || !this.bluetoothDevice.gatt?.connected) {
        this.toast.info('Connecting to Bluetooth printer...');
        const connected = await this.connectBluetoothPrinter();
        if (!connected) {
          this.toast.error('Could not connect to Bluetooth printer. Check Printer Settings.');
          return false;
        }
      }
      return true;
    }

    const needsConnection = !this.serialPort || !this.serialPort.readable || !this.serialPort.writable;
    if (needsConnection) {
      this.toast.info('Connecting to printer...');
      if (this.serialPort && (!this.serialPort.readable || !this.serialPort.writable)) {
        this.serialPort = null;
      }
      const connected = await this.connectUsbOrSerialPrinter();
      if (!connected) {
        this.toast.error('Could not connect to printer. Check Printer Settings.');
        return false;
      }
    }
    return true;
  }

  /**
   * Print product barcode label(s) — one sticker per job so each starts at label top.
   */
  async printBarcodeLabel(labelData: BarcodeLabelData, quantity = 1): Promise<boolean> {
    try {
      const barcode = (labelData.barcode || labelData.sku || '').trim();
      if (!barcode) {
        this.toast.error('Product has no barcode or SKU to print');
        return false;
      }

      const copies = Math.max(1, Math.min(quantity, 99));
      const settings = this.getSettings();
      const protocol: LabelProtocol = settings.labelProtocol === 'tspl' ? 'tspl' : 'escpos';
      const payload = { ...labelData, barcode };

      const ready = await this.ensurePrinterReady();
      if (!ready) {
        return false;
      }

      let printed = 0;
      for (let i = 0; i < copies; i++) {
        const labelBytes = await this.generateBarcodeLabelBytes(payload, protocol, 1);
        const ok = await this.sendToPrinter(labelBytes, settings.connectionType);
        if (!ok) {
          this.toast.error(`Failed after ${printed} label(s). Check connection.`);
          return false;
        }
        printed += 1;
        if (i < copies - 1) {
          await this.delay(PRODUCT_LABEL_PRINT_DELAY_MS);
        }
      }

      const labelWord = copies === 1 ? 'label' : 'labels';
      this.toast.success(`${printed} barcode ${labelWord} sent to printer`);
      return true;
    } catch (error: any) {
      console.error('Barcode label print error:', error);
      this.toast.error('Print error: ' + (error.message || 'Unknown error'));
      return false;
    }
  }

  /**
   * Add label text without ellipsis — clip only if still too long for the sticker width.
   */
  private addLabelText(commands: number[], text: string | null | undefined): void {
    if (!text || this.needsRasterLabelText(text)) {
      return;
    }
    for (let i = 0; i < text.length; i++) {
      const code = text.charCodeAt(i);
      if (code <= 0xFF) {
        commands.push(code);
      }
    }
  }

  /**
   * Add text to commands array
   */
  private addText(commands: number[], text: string | null | undefined, maxLength: number): void {
    if (!text) {
      return;
    }
    const truncated = this.truncate(text, maxLength);
    for (let i = 0; i < truncated.length; i++) {
      commands.push(truncated.charCodeAt(i));
    }
  }

  /**
   * Add a line of text
   */
  private addLine(commands: number[], text: string | null | undefined, maxLength: number): void {
    this.addText(commands, text, maxLength);
    commands.push(0x0A); // Line feed
  }

  /**
   * Add divider line
   */
  private addDivider(commands: number[], charsPerLine: number): void {
    const divider = '-'.repeat(charsPerLine);
    this.addText(commands, divider, charsPerLine);
  }

  /**
   * Truncate text to fit within max length
   */
  private truncate(text: string | null | undefined, maxLength: number): string {
    if (!text) {
      return '';
    }
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength - 3) + '...';
  }

  /**
   * Format amount line with label and right-aligned amount
   */
  private formatAmountLine(label: string, amount: number, charsPerLine: number): string {
    // Use Rs. instead of ₹ symbol
    // Handle negative amounts (for discounts)
    const isNegative = amount < 0;
    const absAmount = Math.abs(amount);
    const amountText = isNegative ? `-Rs.${absAmount.toFixed(2)}` : `Rs.${absAmount.toFixed(2)}`;
    const labelLength = label.length;
    const amountLength = amountText.length;
    const totalLength = labelLength + amountLength;
    
    if (totalLength <= charsPerLine) {
      // Pad amount to align right
      const padding = charsPerLine - labelLength - amountLength;
      return label + ' '.repeat(padding) + amountText;
    } else {
      // Truncate label if needed
      const maxLabelLen = charsPerLine - amountLength;
      const truncatedLabel = this.truncate(label, maxLabelLen);
      return truncatedLabel + amountText;
    }
  }

  private async getEscPosImageBytes(dataUrl: string, maxWidth: number, maxHeight: number): Promise<number[]> {
    if (!this.isBrowser || !dataUrl.startsWith('data:image/')) {
      return [];
    }

    const image = await this.loadImage(dataUrl);
    const widthRatio = maxWidth / image.width;
    const heightRatio = maxHeight / image.height;
    const ratio = Math.min(1, widthRatio, heightRatio);

    const rawWidth = Math.max(8, Math.floor(image.width * ratio));
    const rawHeight = Math.max(8, Math.floor(image.height * ratio));
    const width = rawWidth % 8 === 0 ? rawWidth : rawWidth + (8 - (rawWidth % 8));
    const height = rawHeight;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return [];

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, rawWidth, rawHeight);

    const imageData = ctx.getImageData(0, 0, width, height).data;
    const bytesPerRow = width / 8;
    const rasterBytes: number[] = [];

    for (let y = 0; y < height; y++) {
      for (let xByte = 0; xByte < bytesPerRow; xByte++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = (xByte * 8) + bit;
          const index = ((y * width) + x) * 4;
          const r = imageData[index];
          const g = imageData[index + 1];
          const b = imageData[index + 2];
          const alpha = imageData[index + 3];
          const luminance = (0.299 * r) + (0.587 * g) + (0.114 * b);
          const isBlack = alpha > 10 && luminance < 190;
          if (isBlack) {
            byte |= (0x80 >> bit);
          }
        }
        rasterBytes.push(byte);
      }
    }

    const xL = bytesPerRow & 0xFF;
    const xH = (bytesPerRow >> 8) & 0xFF;
    const yL = height & 0xFF;
    const yH = (height >> 8) & 0xFF;

    return [
      0x1D, 0x76, 0x30, 0x00,
      xL, xH, yL, yH,
      ...rasterBytes
    ];
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = src;
    });
  }

  /**
   * Connect to Bluetooth printer (100% driver-free!)
   */
  async connectBluetoothPrinter(): Promise<boolean> {
    try {
      console.log('[PrinterService] Starting Bluetooth connection...');
      
      if (!('bluetooth' in navigator)) {
        this.toast.error('Bluetooth is not supported in this browser. Please use Chrome or Edge.');
        return false;
      }

      this.toast.info('Searching for Bluetooth printers...');

      // Request Bluetooth device with all common printer services
      const device = await (navigator as any).bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          '000018f0-0000-1000-8000-00805f9b34fb', // Nordic UART Service
          '00001101-0000-1000-8000-00805f9b34fb', // Serial Port Profile (SPP)
          '0000fff0-0000-1000-8000-00805f9b34fb', // Generic printer service
          '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip Transparent UART
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // BLE UART Service
          '6e400001-b5a3-f393-e0a9-e50e24dcca9e'  // Nordic UART Service (alternative)
        ]
      });

      console.log('[PrinterService] Device selected:', device.name);

      if (!device.gatt) {
        this.toast.error('Device does not support Bluetooth Low Energy (BLE)');
        return false;
      }

      this.toast.info('Connecting to ' + (device.name || 'printer') + '...');
      const server = await device.gatt.connect();
      console.log('[PrinterService] GATT server connected');
      
      // List of service UUIDs to try (in order of commonality)
      const serviceUUIDs = [
        '000018f0-0000-1000-8000-00805f9b34fb', // Nordic UART
        '00001101-0000-1000-8000-00805f9b34fb', // SPP
        '0000fff0-0000-1000-8000-00805f9b34fb', // Generic
        '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip
        'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // BLE UART
        '6e400001-b5a3-f393-e0a9-e50e24dcca9e'  // Nordic alternative
      ];

      let service = null;
      let serviceUUID = null;

      // Try each service UUID
      for (const uuid of serviceUUIDs) {
        try {
          console.log('[PrinterService] Trying service:', uuid);
          service = await server.getPrimaryService(uuid);
          serviceUUID = uuid;
          console.log('[PrinterService] Found service:', uuid);
          break;
        } catch (e) {
          // Service not found, try next
        }
      }

      // If no specific service found, try to get all services
      if (!service) {
        console.log('[PrinterService] No known service found, getting all services...');
        try {
          const services = await server.getPrimaryServices();
          console.log('[PrinterService] Available services:', services.length);
          
          if (services.length > 0) {
            // Use the first available service
            service = services[0];
            serviceUUID = service.uuid;
            console.log('[PrinterService] Using first available service:', serviceUUID);
          }
        } catch (e) {
          console.error('[PrinterService] Error getting services:', e);
        }
      }

      if (!service) {
        this.toast.error('Could not find printer service. Your printer may not support BLE printing.');
        return false;
      }

      // List of characteristic UUIDs to try (for writing data)
      const characteristicUUIDs = [
        '00002af1-0000-1000-8000-00805f9b34fb', // Nordic UART TX
        '0000fff2-0000-1000-8000-00805f9b34fb', // Generic write
        '49535343-8841-43f4-a8d4-ecbe34729bb3', // Microchip data
        'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // BLE UART write
        '6e400002-b5a3-f393-e0a9-e50e24dcca9e'  // Nordic UART TX (alternative)
      ];

      let characteristic = null;

      // Try each characteristic UUID
      for (const uuid of characteristicUUIDs) {
        try {
          console.log('[PrinterService] Trying characteristic:', uuid);
          characteristic = await service.getCharacteristic(uuid);
          console.log('[PrinterService] Found characteristic:', uuid);
          break;
        } catch (e) {
          // Characteristic not found, try next
        }
      }

      // If no specific characteristic found, get all and find a writable one
      if (!characteristic) {
        console.log('[PrinterService] No known characteristic found, getting all...');
        try {
          const characteristics = await service.getCharacteristics();
          console.log('[PrinterService] Available characteristics:', characteristics.length);
          
          // Find first writable characteristic
          for (const char of characteristics) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              characteristic = char;
              console.log('[PrinterService] Found writable characteristic:', char.uuid);
              break;
            }
          }
        } catch (e) {
          console.error('[PrinterService] Error getting characteristics:', e);
        }
      }

      if (!characteristic) {
        this.toast.error('Could not find writable characteristic. Printer may not support data transmission.');
        return false;
      }

      // Verify we can write to it
      if (!characteristic.properties.write && !characteristic.properties.writeWithoutResponse) {
        this.toast.error('Characteristic is not writable. Cannot send data to printer.');
        return false;
      }

      this.bluetoothDevice = device;
      console.log('[PrinterService] Bluetooth printer successfully connected!');
      this.toast.success('✅ Bluetooth printer connected! No drivers needed.');
      return true;
    } catch (error: any) {
      console.error('[PrinterService] Bluetooth connection error:', error);
      
      if (error.name === 'NotFoundError') {
        this.toast.error('No Bluetooth device selected. Please try again.');
      } else if (error.name === 'SecurityError') {
        this.toast.error('Bluetooth permission denied. Please allow Bluetooth access.');
      } else if (error.name === 'NetworkError') {
        this.toast.error('Connection failed. Make sure printer is on and in range.');
      } else if (error.name === 'NotSupportedError') {
        this.toast.error('Your browser does not support Web Bluetooth. Use Chrome or Edge.');
      } else {
        this.toast.error('Connection failed: ' + (error.message || 'Unknown error'));
      }
      return false;
    }
  }

  /**
   * Connect to USB or Serial printer
   */
  async connectUsbOrSerialPrinter(forceNewPort: boolean = false): Promise<boolean> {
    try {
      console.log('[PrinterService] Attempting to connect to serial/USB printer...');
      
      if (!('serial' in navigator)) {
        console.error('[PrinterService] Serial API not available');
        this.toast.error('Serial port is not supported in this browser. Please use Chrome or Edge.');
        return false;
      }

      const serial = (navigator as any).serial;

      // Close existing port if open
      if (this.serialPort) {
        console.log('[PrinterService] Closing existing port...');
        try {
          await this.serialPort.close();
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (e) {
          console.warn('[PrinterService] Error closing existing port:', e);
        }
        this.serialPort = null;
      }

      let port = null;
      let savedBaudRate: number | null = null;
      
      // Get saved configuration if available
      if (this.isBrowser) {
        try {
          const savedConfig = localStorage.getItem(this.SERIAL_CONFIG_KEY);
          if (savedConfig) {
            const config = JSON.parse(savedConfig);
            savedBaudRate = config.baudRate;
            console.log('[PrinterService] Found saved baud rate:', savedBaudRate);
          }
        } catch (e) {
          console.warn('[PrinterService] Could not load saved config:', e);
        }
      }
      
      // If not forcing new port, try to get previously granted ports
      if (!forceNewPort) {
        console.log('[PrinterService] Checking for previously granted ports...');
        try {
          const ports = await serial.getPorts();
          console.log('[PrinterService] Found', ports.length, 'previously granted ports');
          
          if (ports.length > 0) {
            // If we have a saved baud rate, try the last successful port with that rate
            if (savedBaudRate && ports.length === 1) {
              console.log('[PrinterService] Using saved port with saved baud rate:', savedBaudRate);
              port = ports[0];
            } else {
              // Try to use existing ports, but check if they're accessible
              for (const existingPort of ports) {
                try {
                  console.log('[PrinterService] Testing existing port...');
                  
                  // Check if port is already open
                  const isOpen = existingPort.readable !== null || existingPort.writable !== null;
                  
                  if (isOpen) {
                    console.log('[PrinterService] Port is already open, using it directly');
                    port = existingPort;
                    // IMPORTANT: Save the port immediately
                    this.serialPort = port;
                    this.toast.success('✅ Printer already connected and ready!');
                    return true;
                  }
                  
                  // Port is closed, try to open it
                  // Use saved baud rate if available, otherwise default
                  const testBaudRate = savedBaudRate || 9600;
                  
                  await existingPort.open({
                    baudRate: testBaudRate,
                    dataBits: 8,
                    stopBits: 1,
                    parity: 'none',
                    bufferSize: 4096,
                    flowControl: 'none'
                  });
                  
                  // If open succeeded, close it and use this port
                  await existingPort.close();
                  await new Promise(resolve => setTimeout(resolve, 300));
                  
                  console.log('[PrinterService] Found accessible port');
                  port = existingPort;
                  break;
                } catch (testError) {
                  console.warn('[PrinterService] Port not accessible:', testError);
                  // This port is not available, try next one
                }
              }
            }
          }
        } catch (e) {
          console.warn('[PrinterService] Error getting ports:', e);
        }
      }

      // If no accessible port found, request a new one
      if (!port) {
        console.log('[PrinterService] Requesting new serial port...');
        this.toast.info('Please select your printer from the list.');
        
        try {
          port = await serial.requestPort();
          console.log('[PrinterService] Port selected');
        } catch (requestError: any) {
          if (requestError.name === 'NotFoundError') {
            this.toast.error('No device selected. Please try again and select your printer.');
            return false;
          }
          throw requestError;
        }
      }
      
      console.log('[PrinterService] Opening port...');
      
      // Try common baud rates for thermal printers
      // If we have a saved baud rate, try it first
      let baudRates = [9600, 19200, 38400, 115200];
      if (savedBaudRate && !baudRates.includes(savedBaudRate)) {
        baudRates.unshift(savedBaudRate);
      } else if (savedBaudRate) {
        // Move saved baud rate to front
        baudRates = [savedBaudRate, ...baudRates.filter(r => r !== savedBaudRate)];
      }
      
      let lastError: any = null;
      
      for (const baudRate of baudRates) {
        try {
          console.log(`[PrinterService] Trying baud rate: ${baudRate}`);
          
          // Open port with current baud rate
          await port.open({
            baudRate: baudRate,
            dataBits: 8,
            stopBits: 1,
            parity: 'none',
            bufferSize: 4096,
            flowControl: 'none'
          });
          
          console.log(`[PrinterService] Port opened successfully at ${baudRate} baud`);
          
          // Verify connection by checking if writable stream is available
          await new Promise(resolve => setTimeout(resolve, 200));
          
          if (port.writable) {
            console.log('[PrinterService] Port is writable, connection successful');
            this.serialPort = port;
            
            // Save successful configuration
            if (this.isBrowser) {
              try {
                localStorage.setItem(this.SERIAL_CONFIG_KEY, JSON.stringify({
                  baudRate: baudRate,
                  timestamp: new Date().toISOString()
                }));
                console.log('[PrinterService] Saved baud rate for next time:', baudRate);
              } catch (e) {
                console.warn('[PrinterService] Could not save config:', e);
              }
            }
            
            this.toast.success(`✅ Printer connected at ${baudRate} baud - Will auto-connect next time!`);
            return true;
          } else {
            console.warn('[PrinterService] Port opened but not writable');
            try {
              await port.close();
              await new Promise(resolve => setTimeout(resolve, 500));
            } catch (e) {
              console.warn('[PrinterService] Error closing non-writable port:', e);
            }
          }
        } catch (openError: any) {
          console.warn(`[PrinterService] Failed at ${baudRate} baud:`, openError.name, openError.message);
          lastError = openError;
          
          // Try to close the port if it's in a bad state
          try {
            await port.close();
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (e) {
            // Ignore close errors
          }
          
          // Continue to next baud rate
        }
      }
      
      // If we get here, all baud rates failed
      if (lastError) {
        throw lastError;
      } else {
        throw new Error('Failed to open port with any baud rate');
      }
    } catch (error: any) {
      console.error('[PrinterService] Serial connection error:', error);
      
      if (error.name === 'NotFoundError') {
        this.toast.error('No serial devices found. Ensure: 1) Printer is connected via USB, 2) Printer is powered on, 3) Drivers are installed.');
      } else if (error.name === 'SecurityError') {
        this.toast.error('Permission denied. Please allow access to the serial port when prompted.');
      } else if (error.name === 'InvalidStateError') {
        this.toast.error('Port is busy or in use. Close other applications using the printer and try again.');
      } else if (error.name === 'NetworkError') {
        this.toast.error('Failed to open port. The port may be in use by another application. Try: 1) Close all apps that might be using the printer, 2) Click "Select New Port" to choose a different port, 3) Unplug and reconnect the printer.');
      } else if (error.name === 'AbortError') {
        this.toast.error('Connection cancelled.');
      } else {
        const errorMsg = error.message || error.name || 'Unknown error';
        this.toast.error('Failed to connect: ' + errorMsg);
      }
      return false;
    }
  }

  /**
   * Force selection of a new serial port
   */
  async selectNewSerialPort(): Promise<boolean> {
    // Clear saved configuration when selecting new port
    if (this.isBrowser) {
      try {
        localStorage.removeItem(this.SERIAL_CONFIG_KEY);
        console.log('[PrinterService] Cleared saved serial configuration');
      } catch (e) {
        console.warn('[PrinterService] Could not clear saved config:', e);
      }
    }
    return this.connectUsbOrSerialPrinter(true);
  }

  /**
   * Clear saved serial port configuration
   */
  clearSerialConfig(): void {
    if (!this.isBrowser) {
      return;
    }

    try {
      localStorage.removeItem(this.SERIAL_CONFIG_KEY);
      this.toast.success('Printer configuration cleared');
      console.log('[PrinterService] Serial configuration cleared');
    } catch (e) {
      console.warn('[PrinterService] Could not clear saved config:', e);
    }
  }

  /**
   * Check for available serial ports (diagnostic)
   */
  async checkAvailablePorts(): Promise<void> {
    try {
      if (!('serial' in navigator)) {
        this.toast.error('Serial API not supported in this browser');
        return;
      }

      const serial = (navigator as any).serial;
      const ports = await serial.getPorts();
      
      console.log('[PrinterService] Available serial ports:', ports.length);
      
      if (ports.length === 0) {
        this.toast.info('No serial ports found. Your printer may be: 1) A Bluetooth printer (use Bluetooth option), 2) Missing drivers, 3) Not connected properly, 4) In wrong USB mode.');
      } else {
        this.toast.success(`Found ${ports.length} serial port(s). Click "Test Connection" to try connecting.`);
      }
      
      // Try to get port info
      for (let i = 0; i < ports.length; i++) {
        const port = ports[i];
        const info = await port.getInfo?.();
        console.log(`[PrinterService] Port ${i}:`, info);
      }
    } catch (error: any) {
      console.error('[PrinterService] Error checking ports:', error);
      this.toast.error('Error checking ports: ' + error.message);
    }
  }

  /**
   * Send ESC/POS bytes to printer
   */
  async sendToPrinter(escPosBytes: Uint8Array, connectionType: ConnectionType): Promise<boolean> {
    try {
      if (connectionType === 'bluetooth') {
        return await this.sendToBluetooth(escPosBytes);
      } else {
        return await this.sendToSerial(escPosBytes);
      }
    } catch (error: any) {
      this.toast.error('Failed to send data to printer');
      console.error('Print error:', error);
      return false;
    }
  }

  /**
   * Send data to Bluetooth printer (100% driver-free!)
   */
  private async sendToBluetooth(escPosBytes: Uint8Array): Promise<boolean> {
    if (!this.bluetoothDevice || !this.bluetoothDevice.gatt?.connected) {
      this.toast.error('Bluetooth printer not connected');
      return false;
    }

    try {
      console.log('[PrinterService] Sending', escPosBytes.length, 'bytes to Bluetooth printer...');
      
      const server = await this.bluetoothDevice.gatt.connect();
      
      // List of service UUIDs to try
      const serviceUUIDs = [
        '000018f0-0000-1000-8000-00805f9b34fb',
        '00001101-0000-1000-8000-00805f9b34fb',
        '0000fff0-0000-1000-8000-00805f9b34fb',
        '49535343-fe7d-4ae5-8fa9-9fafd205e455',
        'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
        '6e400001-b5a3-f393-e0a9-e50e24dcca9e'
      ];

      let service = null;
      for (const uuid of serviceUUIDs) {
        try {
          service = await server.getPrimaryService(uuid);
          break;
        } catch (e) {
          // Try next
        }
      }

      if (!service) {
        // Get first available service
        const services = await server.getPrimaryServices();
        if (services.length > 0) {
          service = services[0];
        }
      }

      if (!service) {
        this.toast.error('Could not find printer service');
        return false;
      }

      // List of characteristic UUIDs to try
      const characteristicUUIDs = [
        '00002af1-0000-1000-8000-00805f9b34fb',
        '0000fff2-0000-1000-8000-00805f9b34fb',
        '49535343-8841-43f4-a8d4-ecbe34729bb3',
        'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
        '6e400002-b5a3-f393-e0a9-e50e24dcca9e'
      ];

      let characteristic = null;
      for (const uuid of characteristicUUIDs) {
        try {
          characteristic = await service.getCharacteristic(uuid);
          break;
        } catch (e) {
          // Try next
        }
      }

      if (!characteristic) {
        // Get first writable characteristic
        const characteristics = await service.getCharacteristics();
        for (const char of characteristics) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            characteristic = char;
            break;
          }
        }
      }

      if (!characteristic) {
        this.toast.error('Could not find writable characteristic');
        return false;
      }

      // Adaptive chunking: faster when writeWithoutResponse is supported.
      const chunkSize = characteristic.properties.writeWithoutResponse ? 128 : 64;
      const interChunkDelayMs = characteristic.properties.writeWithoutResponse ? 5 : 12;
      
      console.log('[PrinterService] Using chunk size:', chunkSize, 'bytes');
      console.log('[PrinterService] Characteristic supports writeWithoutResponse:', characteristic.properties.writeWithoutResponse);

      // Send data in small chunks with delays
      let bytesSent = 0;
      const totalBytes = escPosBytes.length;
      
      for (let i = 0; i < totalBytes; i += chunkSize) {
        const chunk = escPosBytes.slice(i, Math.min(i + chunkSize, totalBytes));
        
        try {
          // ALWAYS use writeWithoutResponse for thermal printers (faster and more reliable)
          if (characteristic.properties.writeWithoutResponse) {
            await characteristic.writeValueWithoutResponse(chunk);
          } else {
            await characteristic.writeValue(chunk);
          }
          
          bytesSent += chunk.length;
          
          // Progress logging
          if (bytesSent % 100 === 0 || bytesSent === totalBytes) {
            console.log(`[PrinterService] Sent: ${bytesSent}/${totalBytes} bytes (${Math.round(bytesSent/totalBytes*100)}%)`);
          }
          
          // Short delay between chunks to avoid printer buffer overflow.
          if (i + chunkSize < totalBytes) {
            await new Promise(resolve => setTimeout(resolve, interChunkDelayMs));
          }
        } catch (writeError: any) {
          console.error('[PrinterService] ❌ Write error at byte', bytesSent, ':', writeError);
          console.error('[PrinterService] Error name:', writeError.name);
          console.error('[PrinterService] Error message:', writeError.message);
          throw writeError;
        }
      }

      console.log('[PrinterService] Successfully sent all', bytesSent, 'bytes!');
      return true;
    } catch (error: any) {
      console.error('[PrinterService] Bluetooth send error:', error);
      this.toast.error('Failed to send data: ' + (error.message || 'Unknown error'));
      return false;
    }
  }

  /**
   * Send data to Serial/USB printer
   */
  private async sendToSerial(escPosBytes: Uint8Array): Promise<boolean> {
    if (!this.serialPort) {
      this.toast.error('Printer not connected');
      return false;
    }

    try {
      if (!this.serialPort.writable) {
        this.toast.error('Printer port is not writable. Please reconnect.');
        return false;
      }

      console.log('[PrinterService] Sending', escPosBytes.length, 'bytes to serial printer...');
      const writer = this.serialPort.writable.getWriter();
      
      try {
        // Send data in chunks to avoid buffer overflow
        const chunkSize = 128; // Faster serial transfer while generally stable
        let totalSent = 0;
        
        for (let i = 0; i < escPosBytes.length; i += chunkSize) {
          const chunk = escPosBytes.slice(i, i + chunkSize);
          await writer.write(chunk);
          totalSent += chunk.length;
          
          // Log progress for larger prints
          if (totalSent % 256 === 0 || totalSent === escPosBytes.length) {
            console.log(`[PrinterService] Sent ${totalSent}/${escPosBytes.length} bytes`);
          }
          
          // Delay between chunks to let printer process
          if (i + chunkSize < escPosBytes.length) {
            await new Promise(resolve => setTimeout(resolve, 8));
          }
        }
        
        console.log('[PrinterService] All bytes sent, waiting for printer to process...');
        
        // IMPORTANT: Wait longer for printer to process and print
        // Thermal printers need time to heat up and print
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('[PrinterService] ✅ All data sent successfully to serial printer');
        console.log('[PrinterService] ⏳ Printer should be printing now... (if nothing prints, printer may need manual button press)');
        return true;
      } finally {
        writer.releaseLock();
      }
    } catch (error: any) {
      console.error('[PrinterService] Serial write error:', error);
      if (error.name === 'InvalidStateError' || error.name === 'NetworkError') {
        this.toast.error('Printer connection lost. Please reconnect.');
        this.serialPort = null;
      } else {
        this.toast.error('Failed to send data to printer: ' + (error.message || 'Unknown error'));
      }
      return false;
    }
  }

  /**
   * Print bill using current settings
   */
  async printBill(billData: BillData): Promise<boolean> {
    try {
      const settings = this.getSettings();
      console.log('[PrinterService] ===== PRINT BILL REQUEST =====');
      console.log('[PrinterService] Print settings from localStorage:', settings);
      console.log('[PrinterService] Connection type:', settings.connectionType);
      console.log('[PrinterService] Paper size:', settings.paperSize);
      console.log('[PrinterService] Current Bluetooth device:', this.bluetoothDevice);
      console.log('[PrinterService] Bluetooth device connected?:', this.bluetoothDevice?.gatt?.connected);
      console.log('[PrinterService] Current Serial port:', this.serialPort);
      console.log('[PrinterService] Serial port writable?:', this.serialPort?.writable ? 'yes' : 'no');
      
      const escPosBytes = await this.generateEscPosBill(billData, settings.paperSize);
      console.log('[PrinterService] ESC/POS bytes generated:', escPosBytes.length, 'bytes');

      const ready = await this.ensurePrinterReady();
      if (!ready) {
        return false;
      }

      // Send to printer
      console.log('[PrinterService] Sending to printer via:', settings.connectionType);
      const success = await this.sendToPrinter(escPosBytes, settings.connectionType);
      console.log('[PrinterService] Print result:', success);
      
      if (success) {
        this.toast.success('✅ Bill printed successfully on thermal printer!');
      } else {
        this.toast.error('❌ Failed to send to thermal printer. Check printer connection.');
      }
      return success;
    } catch (error: any) {
      console.error('Print error:', error);
      this.toast.error('❌ Print error: ' + (error.message || 'Unknown error'));
      return false;
    }
  }

  /**
   * Print multiple product barcode labels — one sticker per job for correct gap alignment.
   */
  async printBarcodeLabelsBulk(labels: BarcodeLabelData[], quantityPerProduct = 1): Promise<boolean> {
    try {
      const validLabels = labels
        .map(label => ({
          ...label,
          barcode: (label.barcode || label.sku || '').trim()
        }))
        .filter(label => !!label.barcode);

      if (validLabels.length === 0) {
        this.toast.error('No products with barcode or SKU to print');
        return false;
      }

      const copies = Math.max(1, Math.min(quantityPerProduct, 99));
      const settings = this.getSettings();
      const protocol: LabelProtocol = settings.labelProtocol === 'tspl' ? 'tspl' : 'escpos';
      const totalLabels = validLabels.length * copies;

      const ready = await this.ensurePrinterReady();
      if (!ready) {
        return false;
      }

      let printed = 0;
      for (const labelData of validLabels) {
        for (let c = 0; c < copies; c++) {
          const labelBytes = await this.generateBarcodeLabelBytes(labelData, protocol, 1);
          const ok = await this.sendToPrinter(labelBytes, settings.connectionType);
          if (!ok) {
            this.toast.error(`Failed after ${printed} of ${totalLabels} label(s). Check connection.`);
            return false;
          }
          printed += 1;
          if (printed < totalLabels) {
            await this.delay(PRODUCT_LABEL_PRINT_DELAY_MS);
          }
        }
      }

      this.toast.success(`${printed} barcode label(s) sent to printer (${validLabels.length} products)`);
      return true;
    } catch (error: any) {
      console.error('Bulk barcode label print error:', error);
      this.toast.error('Print error: ' + (error.message || 'Unknown error'));
      return false;
    }
  }

  /**
   * Send a simple test print to verify printer is working
   */
  async sendTestPrint(): Promise<boolean> {
    console.log('[PrinterService] ===== SENDING TEST PRINT =====');
    
    const settings = this.getSettings();
    if (!this.isThermalEnabled()) {
      this.toast.info('Thermal printer is disabled. Enable it in Printer Settings.');
      return false;
    }
    if (!settings.connectionType) {
      this.toast.error('Please configure printer settings first');
      return false;
    }

    try {
      // Very simple test: Initialize + Text + Feed + Cut
      const testCommands: number[] = [];
      
      // Initialize printer
      testCommands.push(0x1B, 0x40); // ESC @ - Initialize
      
      // Set bold and center
      testCommands.push(0x1B, 0x61, 0x01); // Center
      testCommands.push(0x1B, 0x45, 0x01); // Bold
      
      // Text
      const testText = '*** TEST PRINT ***';
      for (let i = 0; i < testText.length; i++) {
        testCommands.push(testText.charCodeAt(i));
      }
      testCommands.push(0x0A, 0x0A); // Line feeds
      
      // Normal text
      testCommands.push(0x1B, 0x45, 0x00); // Bold off
      const text2 = 'If you see this,';
      for (let i = 0; i < text2.length; i++) {
        testCommands.push(text2.charCodeAt(i));
      }
      testCommands.push(0x0A);
      
      const text3 = 'printer is working!';
      for (let i = 0; i < text3.length; i++) {
        testCommands.push(text3.charCodeAt(i));
      }
      testCommands.push(0x0A, 0x0A, 0x0A, 0x0A, 0x0A);
      
      // FORCE PRINT
      testCommands.push(0x1B, 0x64, 0x05); // Print and feed
      testCommands.push(0x1B, 0x4A, 0x60); // Feed paper
      testCommands.push(0x1D, 0x56, 0x00); // Full cut
      
      const testBytes = new Uint8Array(testCommands);
      console.log('[PrinterService] Test print:', testBytes.length, 'bytes');
      
      // Send test print
      let success = false;
      if (settings.connectionType === 'bluetooth') {
        success = await this.sendToBluetooth(testBytes);
      } else {
        success = await this.sendToSerial(testBytes);
      }
      
      if (success) {
        this.toast.success('✅ Test print sent! Check your printer.');
      }
      
      return success;
    } catch (error: any) {
      console.error('[PrinterService] Test print error:', error);
      this.toast.error('Test print failed: ' + error.message);
      return false;
    }
  }

  /**
   * Check if printer is connected
   */
  isConnected(): boolean {
    const settings = this.getSettings();
    
    if (settings.connectionType === 'bluetooth') {
      return !!(this.bluetoothDevice && this.bluetoothDevice.gatt?.connected);
    } else {
      return !!(this.serialPort && this.serialPort.readable && this.serialPort.writable);
    }
  }

  /**
   * Get connected printer name
   */
  getConnectedPrinterName(): string {
    if (this.bluetoothDevice && this.bluetoothDevice.name) {
      return this.bluetoothDevice.name;
    }
    return 'Thermal Printer';
  }

  /**
   * Disconnect printer
   */
  disconnect(): void {
    if (this.bluetoothDevice && this.bluetoothDevice.gatt?.connected) {
      this.bluetoothDevice.gatt.disconnect();
      this.bluetoothDevice = null;
    }
    if (this.serialPort) {
      this.serialPort.close();
      this.serialPort = null;
    }
  }
}

